import { getQueryParamByName } from "./helpers";

export interface BroadcastMessage<T = any> {
  type: CheckoutMessageType;
  payload: T;
  senderId?: string; // Add sender identification
}

export class SimpleBroadcastChannel {
  private channel: BroadcastChannel | null = null;
  private channelName: ChannelNameType;
  private senderId: string; // Unique ID for this instance

  constructor(channelName: ChannelNameType) {
    this.channelName = channelName;
    // Use 'page' query parameter as sender ID, fallback to timestamp+random if not available
    const pageParam = getQueryParamByName("page");
    this.senderId = pageParam || "checkout";
    this.init();
  }

  private init() {
    if (typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel(this.channelName);
    } else {
      console.warn("BroadcastChannel not supported");
    }
  }

  // Broadcast a message
  broadcast<T>(type: CheckoutMessageType, payload: T): void {
    if (!this.channel) return;

    const message: BroadcastMessage<T> = {
      type,
      payload,
      senderId: this.senderId, // Include sender ID
    };

    this.channel.postMessage(message);
  }

  // Listen to messages with option to exclude own messages
  onMessage<T>(callback: (message: BroadcastMessage<T>) => void, excludeOwnMessages: boolean = true): void {
    if (!this.channel) return;

    this.channel.addEventListener("message", (event) => {
      const message = event.data as BroadcastMessage<T>;
      // Skip messages from the same sender if excludeOwnMessages is true
      if (excludeOwnMessages && message.senderId === this.senderId) {
        return;
      }

      callback(message);
    });
  }

  // Close the channel
  close(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
  }
}

// Factory function
export const createBroadcastChannel = (channelName: ChannelNameType): SimpleBroadcastChannel => {
  return new SimpleBroadcastChannel(channelName);
};

// Predefined message types for checkout-ui
export const CheckoutMessageTypes = {
  USER_LOGGED_OUT: "USER_LOGGED_OUT",
  USER_LOGGED_IN: "USER_LOGGED_IN",
} as const;

export const ChannelName = {
  SHOPFLO_CHANNEL: "shopflo-channel",
};

export type CheckoutMessageType = typeof CheckoutMessageTypes[keyof typeof CheckoutMessageTypes];

export type ChannelNameType = typeof ChannelName[keyof typeof ChannelName];

// Pre-created instance for direct import and usage
export const shopfloBroadcastChannel = createBroadcastChannel(ChannelName.SHOPFLO_CHANNEL);
